import React, { useEffect, useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import closeButtonPic from '../../assets/designSystem/icons/close.svg';
import addressIcon from '../../assets/designSystem/icons/Address.svg';
import './emergency.css';
import MetricsContext from '../../context/MetricsContext';
import PageNames from '../../utils/PageNames';
import PageTypes from '../../utils/PageTypes';
import ButtonNames from '../../utils/ButtonNames';
import { normalizeCountyName } from '../../utils';
import Airplane from '../../assets/designSystem/icons/airplane.svg';
import Ambulance from '../../assets/designSystem/icons/ambulance.svg';
import Hospital from '../../assets/designSystem/icons/hospital.svg';

const contactData = {
  Nairobi: [
    {
      agency: 'Intensive Care Air Ambulance',
      telephone: '020-601772',
    },
    {
      agency: 'AAR Hospital',
      telephone: '+254 730 655000',
    },
    {
      agency: 'OCOA Ambulance',
      telephone: '+254 758692000',
    },
    {
      agency: "St. John's Ambulances",
      telephone: '0721-225285',
    },
    {
      agency: 'Avenue Rescue Services',
      telephone: '020-3743858',
    },
    {
      agency: "Gertrude Children's Hospital",
      telephone: '020 720 6000',
    },
    {
      agency: 'Nairobi East Hospital Ambulance',
      telephone: '0111435797',
    },
    {
      agency: 'Eplus',
      telephone: '0700395395',
    },
    {
      agency: 'RFH',
      telephone: '0741574782',
    },
    {
      agency: 'Mediheal Ambulance Parklands',
      telephone: '0706000888',
    },
    {
      agency: 'Quick Safe Ambulance Nairobi',
      telephone: '0721853796',
    },
    {
      agency: 'Avenue Parklands',
      telephone: '0711060150 / 0711060175',
    },
    {
      agency: 'Urban Safety Ambulance',
      telephone: '0724626156 / 0722162309',
    },
    {
      agency: "St Mary's Hospital Langata Ambulance",
      telephone: '0777663441',
    },
    {
      agency: 'EOC',
      telephone: '1508',
    },
  ],

  Kisii: [
    {
      agency: 'Nyanchwa Ambulance Kisii',
      telephone: '0723623880',
    },
  ],
  Kiambu: [
    {
      agency: 'Neema Ambulance Kahawa Sukari',
      telephone: '0759263586',
    },
    {
      agency: 'Avenue Hospital Kiambu',
      telephone: '0711060800 / 0715869147',
    },
    {
      agency: 'AAR Hospital Kiambu',
      telephone: '0709000000',
    },
  ],

  Kajiado: [
    {
      agency: "Nairobi Women's Kitengela",
      telephone: '0717723677',
    },
  ],
  Nakuru: [
    {
      agency: "Nairobi Women's Nakuru",
      telephone: '0707957840',
    },
    {
      agency: 'NWH Ambulance Nakuru',
      telephone: '070957840',
    },
  ],
  Thika: [
    {
      agency: 'Avenue Thika',
      telephone: '0711060800 / 0715869147',
    },
  ],
};

const REGION_OPTIONS = Object.keys(contactData);

function getInitialRegion() {
  if (localStorage.getItem('country') !== 'Kenya') {
    return 'Nairobi';
  }

  const storedRegion = normalizeCountyName(localStorage.getItem('county'));
  return contactData[storedRegion] ? storedRegion : 'Nairobi';
}

export default function Emergency({ onCloseModal }) {
  const { t } = useTranslation();
  const { updateUserMetric } = useContext(MetricsContext);
  const startDate = useRef(new Date());
  const [selectedRegion, setSelectedRegion] = useState(getInitialRegion);
  const [showRegionPicker, setShowRegionPicker] = useState(false);

  const sessionId = localStorage.getItem('sessionId');
  useEffect(() => {
    updateUserMetric({
      name: PageNames.EMERGENCY_CONTACTS_MODAL,
      pageType: PageTypes.DEFAULT,
    });
  }, [sessionId]);

  const updateMetrics = (btn) => {
    const dateFinal = new Date();
    updateUserMetric({
      name: PageNames.EMERGENCY_CONTACTS_MODAL,
      pageType: PageTypes.DEFAULT,
      duration: Math.abs(dateFinal - startDate.current),
      interactions: [
        {
          name: btn,
          time: dateFinal,
        },
      ],
    });
  };

  const handleContactClick = async (telephone) => {
    let interactionName = 'telephone selected';

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(telephone);
        interactionName = 'telephone copied to clipboard';
      }
    } catch {
      interactionName = 'telephone selected';
    }

    updateMetrics(interactionName);
    window.location.href = `tel:${telephone}`;
  };

  const getIconForAgency = (agency) => {
    const agencyLower = agency.toLowerCase();
    if (agencyLower.includes('ambulance') && agencyLower.includes('hospital')) return Ambulance;
    if (agencyLower.includes('ambulance')) return Ambulance;
    if (agencyLower.includes('hospital')) return Hospital;
    if (agencyLower.includes('air')) return Airplane;
    return Hospital;
  };

  const handleRegionChange = (region) => {
    setSelectedRegion(region);
    setShowRegionPicker(false);
    localStorage.setItem('country', 'Kenya');
    localStorage.setItem('county', region);
    updateMetrics(`region changed:${region}`);
  };

  const regionContacts = contactData[selectedRegion] || contactData.Nairobi;

  return (
    <>
      <div
        className="modal__backdrop"
        role="presentation"
        onClick={() => {
          updateMetrics(ButtonNames.CLOSE);
          onCloseModal();
        }}
      />
      <div className="emergency__popup">
        <div className="emergency__content">
          <div className="emergency__header">
            <button
              type="button"
              className="emergency__change-region-button"
              aria-expanded={showRegionPicker}
              aria-controls="emergency-region-options"
              onClick={() => setShowRegionPicker((current) => !current)}
            >
              <span className="emergency__change-region-icon" aria-hidden="true">
                <img src={addressIcon} alt="" width={16} height={16} />
              </span>
              <span>{t('emergency.changeRegion')}</span>
            </button>
            <button
              type="button"
              className="emergency__close-button"
              aria-label={t('emergency.closeContacts')}
              onClick={() => {
                updateMetrics(ButtonNames.CLOSE);
                onCloseModal();
              }}
            >
              <img src={closeButtonPic} alt="" className="modal__closeBtn emergency__close-icon" />
            </button>
          </div>
          {showRegionPicker && (
            <div
              id="emergency-region-options"
              className="emergency__region-picker"
              role="group"
              aria-label={t('emergency.regionPickerLabel')}
            >
              {REGION_OPTIONS.map((region) => (
                <button
                  type="button"
                  key={region}
                  className={`emergency__region-option ${
                    selectedRegion === region ? 'emergency__region-option--selected' : ''
                  }`}
                  aria-pressed={selectedRegion === region}
                  onClick={() => handleRegionChange(region)}
                >
                  {region}
                </button>
              ))}
            </div>
          )}
          <div className="emergency__summary">
            <h3 className="emergency__title">{t('emergency.title')}</h3>
            <p className="emergency__region">{t('emergency.regionLabel', { region: selectedRegion })}</p>
          </div>
          <div>
            {regionContacts?.map((contact) => (
              <button
                type="button"
                className="emergency__contact"
                key={contact.agency}
                onClick={() => handleContactClick(contact.telephone)}
              >
                <div className="emergency__contact__header">
                  <img src={getIconForAgency(contact.agency)} alt="" width={16} height={16} />
                  <p className="emergency__contact__name">{contact.agency}</p>
                </div>
                <p>{contact.telephone}</p>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
